import Logger from '../logger';
import event from '../mixins/event';

class ThemeContentModal {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * Class constructor
	 */
	constructor(dom) {

		this.NAME = 'ThemeContentModal';

		this.dom = dom;
		this.domActiveClass = 'theme-content-modal--active';
		this.triggerSelector = '[data-theme-content-modal-trigger]';
		this.boundTriggers = new WeakSet();
		this.triggerObserver = null;
	}

	/**
	 * init - Initialize the component
	 **/
	init() {
		Logger.info(`${this.NAME} > init`);

		this.bindTriggerEvents();

		// Listen for window event to open
		window.addEventListener('theme-content-modal:show', (event) => {
			this.show(event.detail.postId, event.detail.metadata);
		});

		// Listen for window event to close
		window.addEventListener('theme-content-modal:hide', () => {
			this.hide();
		});

		// Listen for clicks outside of .theme-content-modal__frame
		this.dom.addEventListener('click', (event) => {
			if (!this.dom.querySelector('.theme-content-modal__frame').contains(event.target)) {
				this.hide();
			}
		});

		// Look for query params in the url
		const urlParams = new URLSearchParams(window.location.search);
		const modalContentId = urlParams.get('modal-content-id');
		const modalContentMetadata = urlParams.get('modal-content-metadata');
		if (modalContentId) {
			let urlDecodedMetadata = null;
			if (modalContentMetadata) {
				try {
					// Decode the URL-encoded string, then parse the JSON
					const decodedString = decodeURIComponent(modalContentMetadata);
					urlDecodedMetadata = JSON.parse(decodedString);
				} catch (error) {
					Logger.error(`${this.NAME} > Failed to parse modal content metadata:`, error);
					urlDecodedMetadata = null;
				}
			}
			this.show(modalContentId, urlDecodedMetadata);
		} else {
			this.hide();
		}
	}

	/**
	 * bindTriggerEvents
	 */
	bindTriggerEvents() {
		this.bindTriggers(document.querySelectorAll(this.triggerSelector));

		if (typeof MutationObserver === 'undefined' || !document.body) {
			return;
		}

		this.triggerObserver = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (!mutation.addedNodes || !mutation.addedNodes.length) {
					return;
				}

				mutation.addedNodes.forEach((node) => {
					if (!(node instanceof Element)) {
						return;
					}

					if (node.matches(this.triggerSelector)) {
						this.bindTriggers([node]);
					}

					const descendants = node.querySelectorAll(this.triggerSelector);
					if (descendants.length) {
						this.bindTriggers(descendants);
					}
				});
			});
		});

		this.triggerObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	/**
	 * bindTriggers
	 */
	bindTriggers(triggers) {
		Array.from(triggers).forEach((trigger) => {
			if (this.boundTriggers.has(trigger)) {
				return;
			}

			trigger.addEventListener('click', this.handleTriggerClick.bind(this));
			this.boundTriggers.add(trigger);
		});
	}

	/**
	 * handleTriggerClick
	 */
	handleTriggerClick(event) {
		event.preventDefault();

		const trigger = event.currentTarget;
		const postId = trigger.getAttribute('data-theme-content-modal-post-id');
		const metadata = trigger.getAttribute('data-theme-content-modal-metadata');
		if (!postId) {
			return;
		}

		window.dispatchEvent(new CustomEvent('theme-content-modal:show', {
			detail: {
				postId: parseInt(postId, 10),
				metadata: metadata ? JSON.parse(metadata) : null,
			},
		}));
	}

	/**
	 * loadModalContent
	 */
	async loadModalContent(postId, metadata) {
		Logger.info(`${this.NAME} > loadModalContent`);

		var formData = new FormData();

		formData.append('action', 'wonder_get_modal_content');
		formData.append('nonce', window.global_vars.ajax_nonce);
		formData.append('post_id', postId);
		if (metadata) {
			console.log('metadata', metadata);
			formData.append('metadata', JSON.stringify(metadata));
		}

		const url = `${window.global_vars.ajax_url}`;

		// Fetch from wp-admin
		const response = await fetch(url, {
			method: 'POST',
			body: formData,
		});
		const data = await response.json();

		this.renderContent(data.html);
	}

	/**
	 * renderContent - Render the content
	 */
	renderContent(html) {
		Logger.info(`${this.NAME} > renderContent`);
		const contentDom = this.dom.querySelector('[data-theme-content-modal-content]');
		if (!contentDom) return;

		// Clear the content
		contentDom.innerHTML = '';

		// Insert the new content
		contentDom.innerHTML = html;

		// Find the content modal close button and add an event listener to it
		const closeButtonDom = this.dom.querySelector('[data-theme-content-modal-close]');
		if (closeButtonDom) {
			closeButtonDom.addEventListener('click', () => {
				this.hide();
			});
		}
	}

	/**
	 * show - Show the modal
	 */
	show(postId, metadata) {
		Logger.info(`${this.NAME} > show`);
		if (!postId) {
			return;
		}

		this.loadModalContent(postId, metadata);
		this.dom.classList.add(this.domActiveClass);

		// Quietly add query param to the url
		const url = new URL(window.location.href);
		url.searchParams.set('modal-content-id', postId);
		if (metadata) {
			// Stringify the metadata object - URLSearchParams will handle URL encoding
			const jsonMetadata = JSON.stringify(metadata);
			url.searchParams.set('modal-content-metadata', jsonMetadata);
		}
		window.history.replaceState({}, '', url.toString());

		// Turn off body scrolling with event body-scroll:stop
		window.dispatchEvent(new CustomEvent('body-scroll:start'));
	}

	/**
	 * hide - Hide the modal
	 */
	hide() {
		Logger.info(`${this.NAME} > hide`);
		this.dom.classList.remove(this.domActiveClass);

		// Quietly remove query param from the url
		window.history.replaceState({}, '', window.location.pathname);

		// Turn on body scrolling with event body-scroll:start
		window.dispatchEvent(new CustomEvent('body-scroll:stop'));
	}
}

Object.assign(ThemeContentModal.prototype, event);

export default ThemeContentModal;
